import React, { useCallback, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../../../notifications';
import { UnsignDocumentConfirmationModal } from './UnsignDocumentConfirmationModal';
import { useDocumentState } from '../../../../contexts/DocumentContext';
import { documentStateFromResponse } from '../../../../utils/document-wizard-utils/document-state-from-response';
import { cancelSigningDocument } from '../../../../utils/api/cancel-signing-document';
import { DocumentStatusEnum } from '../../../../shared/constants/document';

import { paths } from '../../../../routing';

const UNSIGN_DOCUMENT_ERROR_MESSAGE = "Unable to remove the signature.";

export const UnsignDocumentConfirmationModalContainer = ({
  show,
  onHide,
  dealId,
  teamId,
  documentId,
}: {
  show: boolean;
  onHide: any;
  teamId: string | null;
  dealId: string;
  documentId?: string;
}) => {
  const [isUnsignPending, setIsUnsignPending] = useState<boolean>(false);
  const optionalMessageInputRef = useRef<HTMLTextAreaElement>(null);
  const { state, updateDocumentState, resetDocumentState } = useDocumentState();
  const apolloClient = useApolloClient();
  const navigate = useNavigate();

  const currentDocumentId = state.id;

  const [, { error: notifyError, success: notifySuccess }] = useNotifications();

  const handleError = useCallback(() => {
    notifyError({
      message: UNSIGN_DOCUMENT_ERROR_MESSAGE,
    });
  }, [notifyError]);

  const handleEdit = () => {
    const selectedDocumentId = documentId ? documentId : currentDocumentId;
    const additionalMessage = optionalMessageInputRef.current?.value;
    setIsUnsignPending(true);
    cancelSigningDocument({ documentId: selectedDocumentId!, additionalMessage })(apolloClient)
      .then((response) => {
        const isInReviewingState = response.status === DocumentStatusEnum.PREPARING_TO_SIGN;

        if (!isInReviewingState) {
          handleError();
          return;
        }

        resetDocumentState();
        updateDocumentState(documentStateFromResponse(response));

        notifySuccess({
          message: {
            id: 'phrase.unsign-document.success',
            defaultMessage: 'Signing for {name} cancelled.',
            values: { name: response.title },
          },
        });

        const signDocumentUrl = paths.Documents.signDocument
          .replace(':documentId', selectedDocumentId as string)
          .replace(':teamId', teamId as string)
          .replace(':dealId', dealId);

        // Full page reload to ensure clean state after cancel signing
        window.location.href = signDocumentUrl;
      })
      .catch(() => {
        handleError();
      })
      .finally(() => {
        setIsUnsignPending(false);
        onHide();
      });
  };

  return (
    <UnsignDocumentConfirmationModal
      handleEdit={handleEdit}
      show={show}
      onHide={onHide}
      isUnsignPending={isUnsignPending}
      optionalMessageInputRef={optionalMessageInputRef}
    />
  );
};
